import { z } from 'zod'

import { customProjectIconSchema } from '@/project'
import { externalAccessSchema } from '@/team/access'
import { featureFlagSchema } from '@/team/feature-flags'
import {
  emailSchema,
  entitySchema,
  namespaceSchema,
  nanoidSchema,
  schemaModel,
  slugSchema,
} from '@/utility'

export enum TeamUserRole {
  /** The owner of the team.
   *
   * Permissions:
   * - Add new admins
   * - Switch to a new owner
   * - Billing management
   * - Delete Team
   */
  Owner = 'owner',
  /**
   * Admins
   *
   * Permissions:
   * - Add new members
   * - Remove members
   * - Delete projects and documents
   */
  Admin = 'admin',
  /**
   * Members
   *
   * Permissions:
   * - View the team and its projects
   * - Create new documents
   */
  Editor = 'editor',
  /**
   * Viewers have readonly access to the team and its projects
   *
   * Eventually we may allow extending permissions on a fine-grained level
   */
  Viewer = 'viewer',
}

export const roleLabels: Record<TeamUserRole, string> = {
  [TeamUserRole.Admin]: 'Admin',
  [TeamUserRole.Editor]: 'Editor',
  [TeamUserRole.Owner]: 'Owner',
  [TeamUserRole.Viewer]: 'Viewer',
} as const

// ---------------------------------------------------------------------------

export const roleSchema: z.ZodType<TeamUserRole> = z
  .enum(TeamUserRole)
  .meta({ id: 'role' })

export type UserRef = {
  uid: string
  role: TeamUserRole
  displayName: string
  imageUri?: string
  activeProjectUid: string
  /** For display purposes only - **Not for permissions checks** */
  nonBillable?: boolean
}

export const userRefSchema = z
  .object({
    uid: nanoidSchema,
    role: roleSchema,
    displayName: z.string(),
    imageUri: z.string().optional(),
    activeProjectUid: z.string().default(''),
    nonBillable: z.boolean().default(false).optional(),
  })
  .meta({ id: 'user-ref' }) satisfies z.ZodType<UserRef>

// ---------------------------------------------------------------------------

export enum BillingPlan {
  /** Trial plan */
  Trial = 'trial',
  /** Free plan */
  Free = 'free',
  /** Pro (Monthly) plan */
  ProMonthly = 'pro_monthly',
  /** Pro (Yearly) plan */
  ProYearly = 'pro_yearly',
  /** Premium plan @deprecated The premium plan has been deprecated */
  Premium = 'premium',
  /** Enterprise plan */
  Enterprise = 'enterprise',
}

export const billingPlanLabels: {
  [key in BillingPlan]: string
} = {
  [BillingPlan.Free]: 'Free',
  [BillingPlan.Trial]: 'Trial',
  [BillingPlan.ProMonthly]: 'Monthly',
  [BillingPlan.Premium]: 'Monthly',
  [BillingPlan.ProYearly]: 'Yearly',
  [BillingPlan.Enterprise]: 'Enterprise',
}

export const billingPlanSchema = z
  .enum(BillingPlan)
  .meta({ id: 'billing-plan' })

export type BillingInfo = {
  billingId?: string
  /** The plan the team is on */
  plan: z.infer<typeof billingPlanSchema>
  /** The email of the team */
  email: string
  /**
   * Unix timestamp of expiry date in seconds
   *
   * Set to -1 if there is no expiry or they are currently subscribed
   * otherwise set to a number for when the trial expires or when a
   * cancelled subscription ends
   */
  expires?: number
}

export const billingInfoSchema = z
  .preprocess(
    (arg: any) => {
      if (
        typeof arg?.hasTrial === 'boolean' &&
        ![
          BillingPlan.Premium,
          BillingPlan.ProMonthly,
          BillingPlan.ProYearly,
        ].includes(arg?.plan)
      ) {
        arg.plan = 'trial'
      }
      return arg
    },
    z.object({
      billingId: nanoidSchema.optional(),
      email: emailSchema,
      plan: billingPlanSchema,
      expires: z.number().optional(),
    }),
  )
  .meta({ id: 'billing-info' }) satisfies z.ZodType<BillingInfo>

export const invoiceStatusSchema = z
  .enum(['open', 'paid', 'draft', 'void', 'uncollectible'])
  .meta({ id: 'invoice-status' })

export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>

export const invoiceStatusLabels: {
  [key in InvoiceStatus]: string
} = {
  'open': 'Open',
  'paid': 'Paid',
  'void': 'Void',
  'draft': 'Draft',
  'uncollectible': 'Uncollectible',
}

export const billingInvoiceSchema = z
  .object({
    id: z.string(),
    /** Total invoice payment amount */
    total: z.number(),
    /** Invoice status - we should only display open and paid to users  */
    status: invoiceStatusSchema.nullable(),
    /** Invoice payment date */
    paymentDate: z.number(),
    /** Currency for payment */
    currency: z.string(),
    /** PDF download link */
    downloadUrl: z.string().nullable().optional(),
  })
  .meta({ id: 'billing-invoice' })

export type BillingInvoice = z.infer<typeof billingInvoiceSchema>

export const cardSchema = z
  .object({
    last4: z.string().length(4),
    expMonth: z.number(),
    expYear: z.number(),
  })
  .meta({ id: 'card' })

export type BillingCard = z.infer<typeof cardSchema>

export const teamBillingSummary = z
  .object({
    /** All invoices for a team */
    invoices: billingInvoiceSchema.array().default([]),
    /** Stripe customer billing address */
    address: z.string().nullable(),
    card: cardSchema.nullable(),
    /** Basic Scalar billing info from team record */
    billingInfo: billingInfoSchema,
  })
  .meta({ id: 'team-billing-summary' })

export type TeamBillingSummary = z.infer<typeof teamBillingSummary>

// ---------------------------------------------------------------------------

export const pendingInviteSchema = z
  .object({
    uid: nanoidSchema,
    role: roleSchema.default(TeamUserRole.Editor),
    email: emailSchema,
    expires: z.number().optional(),
  })
  .meta({ id: 'pending-invite' })

export type PendingInvite = z.infer<typeof pendingInviteSchema>

// ---------------------------------------------------------------------------

export const teamNameSchema = z
  .string()
  .trim()
  .overwrite((s) => (s.length ? s : 'Untitled Team'))
  .meta({ id: 'team-name' })

export const teamImageSchema = z.string().optional().meta({ id: 'team-image' })

/**
 * Onboarding state
 *
 * This is in an enum in case
 * we decide to add extra steps
 * to it.
 */
export enum TeamOnboardingState {
  Setup = 'setup',
  Complete = 'complete',
}

export enum TeamAuthenticationMethod {
  Password = 'password',
  Google = 'google',
  GitHub = 'github',
  SSO = 'sso',
}

export const teamAuthenticationMethodLabels: {
  [key in TeamAuthenticationMethod]: string
} = {
  [TeamAuthenticationMethod.Google]: 'Google OAuth',
  [TeamAuthenticationMethod.GitHub]: 'GitHub OAuth',
  [TeamAuthenticationMethod.Password]: 'Email & Password',
  [TeamAuthenticationMethod.SSO]: 'Single Sign-On',
}

export const teamAuthenticationMethodsSchema = z
  .object({
    [TeamAuthenticationMethod.Password]: z.boolean().default(true),
    [TeamAuthenticationMethod.Google]: z.boolean().default(true),
    [TeamAuthenticationMethod.GitHub]: z.boolean().default(true),
    [TeamAuthenticationMethod.SSO]: z.boolean().default(true),
  })
  .meta({ id: 'team-authentication-methods' })

export type TeamAuthenticationMethods = z.infer<
  typeof teamAuthenticationMethodsSchema
>

export const teamAuthenticationSchema = z
  .object({
    /** General team level enabled authentication methods */
    methods: teamAuthenticationMethodsSchema.default(
      schemaModel({}, teamAuthenticationMethodsSchema),
    ),
    /**
     * Allowlist of domains for login.
     *
     * If empty, all domains are allowed.
     */
    domains: z.string().array().default([]),
    /**
     * User level authentication methods.
     *
     * Overrides general team methods that were
     * enabled/disabled.
     */
    userControls: z
      .record(nanoidSchema, teamAuthenticationMethodsSchema)
      .default({}),
  })
  .meta({ id: 'team-authentication' })

export type TeamAuthentication = z.infer<typeof teamAuthenticationSchema>

export const teamSchema = entitySchema
  .extend({
    name: teamNameSchema,
    slug: slugSchema,
    archived: z.boolean().optional().default(false),
    imageUri: teamImageSchema,
    theme: z.string().default('default'),
    onboarding: z
      .enum(TeamOnboardingState)
      .default(TeamOnboardingState.Complete),
    billingInfo: billingInfoSchema,
    namespaces: namespaceSchema
      .array()
      .refine((ns) => !ns.includes('share'), {
        message: '"share" is a reserved namespace and cannot be used',
      })
      .default([]),
    users: userRefSchema.array(),
    icons: customProjectIconSchema.array().default([]),
    pendingInvites: pendingInviteSchema.array().default([]),
    /** Team auth configuration */
    authentication: teamAuthenticationSchema.default(
      schemaModel({}, teamAuthenticationSchema),
    ),
    externalAccess: externalAccessSchema.default(
      schemaModel({}, externalAccessSchema),
    ),
    features: featureFlagSchema.default({}),
    /**
     * Teams can be manually verified when they need to upload documents
     * containing prohibited text or links. This is manually set by request.
     */
    verified: z.boolean().default(false).optional(),
  })
  .meta({ id: 'team' })

export type Team = z.infer<typeof teamSchema>
